import { makeAutoObservable, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import {
  CustomFieldControl,
  DynamicCustomFieldsDefaultType,
} from '@insurancemenu/imenu-components-lib/dist/utils/forms';

export interface RateCap {
  id: string;
  startDate: string;
  endDate: string;
  capPercentage: string;
}

const INITIAL_RATE_CAP_VALIDATION = Object.freeze({
  newRateCapDateRange: '',
  newRateCapPercentage: '',
});

export interface CustomPlanRateCapFields extends DynamicCustomFieldsDefaultType {
  rateCaps: CustomFieldControl<RateCap[]>;
}

export default class CustomPlanRateCapStore {
  rootStore: RootStore;

  rateCaps: RateCap[];

  validationObject: Record<string, string> = INITIAL_RATE_CAP_VALIDATION;

  getRateCaps(): RateCap[] {
    return this.rateCaps;
  }

  hasInvalidFields(): boolean {
    return Object.keys(this.validationObject).some((key) => !!this.validationObject[key]);
  }

  validateField(key: string): string {
    return this.validationObject[key];
  }

  setupInitialValidationsForNewCap(): void {
    this.validationObject = INITIAL_RATE_CAP_VALIDATION;
  }

  saveRateCap(newRateCap: RateCap | undefined): void {
    if (!newRateCap) return;
    this.rateCaps.push(newRateCap);
  }

  removeRateCap(): void {
    this.rateCaps.pop();
  }

  setNewRateCapInvalid(message: string): void {
    this.validationObject.newRateCapDateRange = message;
  }

  isNewRateCapValid(newRateCap: RateCap | undefined): boolean {
    if (!newRateCap || newRateCap.startDate.length === 0 || newRateCap.endDate.length === 0) {
      this.setNewRateCapInvalid('Invalid dates');
      return false;
    }
    if (new Date(newRateCap.startDate).getTime() >= new Date(newRateCap.endDate).getTime()) {
      this.setNewRateCapInvalid('Date range malfunctioned');
      return false;
    }
    if (new Date(newRateCap.startDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
      this.setNewRateCapInvalid('Date cannot be in the past');
      return false;
    }
    const lastRateCap = this.getLastRateCap();
    if (lastRateCap && new Date(lastRateCap.endDate).getTime() >= new Date(newRateCap.startDate).getTime()) {
      this.setNewRateCapInvalid('Overlap with existing');
      return false;
    }
    if (
      lastRateCap &&
      new Date(newRateCap.startDate).getTime() - new Date(lastRateCap.endDate).getTime() > 1000 * 60 * 60 * 24
    ) {
      this.setNewRateCapInvalid('Rate Caps cannot have gaps');
      return false;
    }
    this.setNewRateCapInvalid('');
    return true;
  }

  validateNewRateCapPercentage(percentage: string): void {
    const percentageRegexCheck = /^(0|[1-9]\d*)(\.\d{1,2})?$/.test(percentage);
    if (!percentageRegexCheck) {
      this.validationObject.newRateCapPercentage = 'Valid formats x, x.x, x.xx';
      return;
    }
    this.validationObject.newRateCapPercentage = '';
  }

  setDefaultValues(initialRateCaps: CustomFieldControl<RateCap[]> | undefined): void {
    if (initialRateCaps && initialRateCaps.value.length > 0)
      this.rateCaps = initialRateCaps.value.map((cap) => toJS(cap)).slice();
    this.setupInitialValidationsForNewCap();
  }

  private getLastRateCap(): RateCap {
    return this.rateCaps[this.rateCaps.length - 1];
  }

  resetInitialValues(): void {
    this.rateCaps = [];
    this.validationObject = INITIAL_RATE_CAP_VALIDATION;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.rateCaps = [];
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
